import { MapViewerNode } from '@3ddv/dvm-internal';
import { SectionModel } from '@3ddv/igsu-models/definitions/section.model';
import { MapViewerComponent } from '@3ddv/ngx-dvm-internal';
import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription, catchError, of, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { EventsService } from '../../../events/data-access/events.service';
import { EventsTicketsService } from '../../../events/data-access/events-tickets.service';
import { PriceScalesService } from '../../../events/data-access/price-scales.service';
import { FilterService } from '../../../seat-selection/data-access/filter.service';
import { SeatSelectionState } from '../../../seat-selection/data-access/seat-selection.state';
import { getMessageError } from '../../utils/get-message-error';
import { SectionsAPI } from '../_sections.api';
import { ComputedAvailability, ExchangeService } from '../exchange.service';
import { ModalService } from '../modal.service';
import { SectionsService } from '../sections.service';
import { TimerService } from '../timer.service';
import { SeatViewerService } from './seat-viewer.service';

/**
 * ViewerService is an injectable service that handles the interaction and management
 * of a map viewer component used for venue or section visualization and selection.
 * It leverages several other services like EventsService, FilterService, TimerService,
 * and SectionsService to manage viewer state, handle user interactions, and display
 * accurate and interactive data. It also integrates with a proprietary map viewer
 * library '@3ddv/ngx-dvm-internal' for specific map viewer operations.
 */
@Injectable({
  providedIn: 'root',
})
export class SectionViewerService {
  protected _ngZone = inject(NgZone);
  private _router = inject(Router);
  private _injector = inject(Injector);
  private _sectionsApi = inject(SectionsAPI);
  private _modalService = inject(ModalService);
  private _eventService = inject(EventsService);
  private _eventTicketsService = inject(EventsTicketsService);
  private _filterService = inject(FilterService);
  private _timerService = inject(TimerService);
  private _sectionsService = inject(SectionsService);
  private _exchangeService = inject(ExchangeService);
  private _priceScalesService = inject(PriceScalesService);
  private _seatSelectionState = inject(SeatSelectionState);

  private readonly subscriptions: Subscription[] = [];

  public viewer2DRef: MapViewerComponent | null = null;
  public hasSeatMapEnabled = false;

  public _isMapLoading = new BehaviorSubject(false);
  public isMapLoading$ = this._isMapLoading.asObservable();

  /**
   * Initializes the viewer service by setting up the viewer and registering necessary subscriptions.
   * @param viewer The MapViewerComponent instance to be used and managed.
   */
  public initialize = (viewer: MapViewerComponent) => {
    const client_id = environment.production ? 'tdc' : 'tdc_sandbox';

    const initSubscription = viewer.service
      .initialize({ client_id })
      .pipe(catchError(this.onTopviewProviderError))
      .subscribe(() => this.setupViewer(viewer));
    this.viewer2DRef = viewer;
    // @ts-ignore
    window['viewer'] = this.viewer2DRef;
    this.subscriptions.push(initSubscription);
  };

  /**
   * Cleans up and unsubscribes from all subscriptions when the viewer is no longer needed to avoid memory leaks.
   */
  public cleanSubscriptions = () => {
    for (const s of this.subscriptions) {
      s.unsubscribe();
    }
  };

  /**
   * Sets the availability of map sections based on given data.
   * @param availabilityData Array of SectionModels indicating availability of sections.
   */
  public setTopviewAvailability = (availabilityData: ComputedAvailability[]) => {
    const sectionIDArray = availabilityData
      .filter(this._filterService.filterSectionByRange)
      .map((filteredSection: ComputedAvailability) => {
        return filteredSection?.sectionId.toString();
      });

    const generalAdmissionSuccess = this.viewer2DRef?.service.setAvailability('general_admission', sectionIDArray);
    const sectionSuccess = this.viewer2DRef?.service.setAvailability('section', sectionIDArray);
    return sectionSuccess || generalAdmissionSuccess;
  };

  /**
   * Narrows computed availability to the entries the viewer can actually offer: those inside the
   * current price filter range and backed by a node of the loaded map.
   *
   * The availability endpoint can return sections that have no counterpart on the venue map, and
   * `setAvailability` silently discards those ids. Consumers that render availability outside the
   * map (such as the list view) must apply the same narrowing, otherwise they offer sections that
   * cannot be selected.
   *
   * @param availabilityData The computed availability data to narrow.
   * @returns The availability entries that map to a selectable map node.
   */
  public getSelectableAvailability = (availabilityData: ComputedAvailability[] | null): ComputedAvailability[] => {
    if (!availabilityData?.length) {
      return [];
    }

    const inPriceRange = availabilityData.filter(this._filterService.filterSectionByRange);
    const viewerService = this.viewer2DRef?.service;

    if (!viewerService) {
      return inPriceRange;
    }

    return inPriceRange.filter(item => !!viewerService.getNodeById(item.sectionId.toString()));
  };

  /**
   * Unselects all selected map sections in the viewer.
   */
  public unselectNode = (nodeId: string) => {
    return this.viewer2DRef?.service.unselect(nodeId);
  };

  /**
   * Unselects all selected map sections in the viewer.
   */
  public unselectAllMapSections = () => {
    return this.viewer2DRef?.service.unselectAll();
  };

  /**
   * Selects a specific map section based on the provided node ID.
   * @param nodeID The identifier of the map viewer node to select.
   */
  public selectNode = (nodeID: MapViewerNode['id']) => {
    return this.viewer2DRef?.service.select(nodeID);
  };

  /**
   * Returns whether the map availability has been already set
   */
  public isAvailabilityOnMapSet = () => {
    return this.viewer2DRef?.service.getNodes({ state: 'available' }).length;
  };

  public setIsMapLoading = (value: boolean) => {
    return this._isMapLoading.next(value);
  };

  /**
   * Sets up the viewer by subscribing to necessary viewer events like map load, success, and click events.
   * @param viewer The MapViewerComponent instance to be set up.
   */
  public setupViewer = (viewer: MapViewerComponent) => {
    this.subscribeToTopViewLoadMap(viewer);
    this.subscribeToTopViewLoadSuccess(viewer);
    this.subscribeToClick(viewer);
  };

  /**
   * Retrieves necessary map data to load the map viewer.
   * @returns Object containing the venue_id and map_id required to load the map.
   */
  public getMapData = () => {
    const eventConfiguration = this._eventService.getEventConfiguration();
    const venue_id = eventConfiguration?.venueId;
    const map_id = eventConfiguration?.mapId;
    // @ts-ignore
    const hasSeatMapEnable = eventConfiguration?.seatMap;
    this.hasSeatMapEnabled = hasSeatMapEnable ?? false;

    if (!venue_id || !map_id) {
      return;
    }

    return {
      venue_id,
      map_id,
      hasSeatMapEnable,
    };
  };

  /**
   * Subscribes to viewer's load success event to configure viewer flags and fetch section availability.
   * @param viewer The MapViewerComponent instance being observed.
   */
  private subscribeToTopViewLoadSuccess = (viewer: MapViewerComponent) => {
    this.setIsMapLoading(true);
    const subscription = viewer.service
      .getObservable('load_success')
      .pipe(catchError(this.onTopviewProviderError))
      .subscribe(() => {
        this.configureTopViewFlags(viewer);
        const availableSectionData = this._sectionsService.getAvailableSectionData();

        if (availableSectionData) {
          this.setSectionAvailability(availableSectionData);
        } else {
          this.fetchAndSetSectionAvailability();
        }

        const seatViewerService = this._injector.get(SeatViewerService);

        if (seatViewerService.sectionWithSeatsSelected) {
          viewer.service.select(seatViewerService.sectionWithSeatsSelected);
        }
        this.setupGeneralAdmissionSections(viewer);
      });
    this.subscriptions.push(subscription);
  };

  /**
   * Subscribes to load map event to handle specific actions when the map is loaded, especially post-checkout cancellation.
   * @param viewer The MapViewerComponent instance being observed.
   */
  private subscribeToTopViewLoadMap = (viewer: MapViewerComponent) => {
    const mapData = this.getMapData();
    if (!mapData) {
      throw new Error('Map data was incorrect or not provided');
    }

    const subscription = viewer.service
      .loadMap(mapData)
      .pipe(catchError(this.onTopviewProviderError))
      .subscribe(() => {
        if (this._timerService.isCheckoutCanceled) {
          this.onLoadFromCheckoutCanceled();
        }
      });

    this.subscriptions.push(subscription);
  };

  /**
   * Subscribes to click events on the map viewer to handle user interactions with map nodes.
   * @param viewer The MapViewerComponent instance being observed.
   */
  private subscribeToClick = (viewer: MapViewerComponent) => {
    const subscription = viewer.service.getObservable('click').subscribe(viewerEvent => {
      const [node] = viewerEvent.event.nodes;
      const isGeneralAdmissionSection = this._sectionsService.isGeneralAdmissionSection(node?.id);
      if (this.hasSeatMapEnabled && !isGeneralAdmissionSection) {
        const seatViewerService = this._injector.get(SeatViewerService);
        seatViewerService.handleClickOnSeatMapEnabled(node, viewer);
      } else {
        this.handleClickOnBestAvailableEnabled(node);
      }
    });
    this.subscriptions.push(subscription);
  };

  /**
   * Handles click events on the map viewer, selecting sections and updating services accordingly.
   * @param viewerEvent The event object containing details about the click event.
   */
  private handleClickOnBestAvailableEnabled = (node: MapViewerNode) => {
    if (!node?.id || node.type !== 'section' || node.state !== 'available') {
      return;
    }

    this._ngZone.run(() => {
      this.unselectAllMapSections();
      this._sectionsService.selectSection(node);
      this._priceScalesService.selectPriceScaleFromNode(node);
    });
  };

  /**
   * Handles actions to be taken when the viewer is reloaded after checkout cancellation.
   */
  public onLoadFromCheckoutCanceled = () => {
    if (this.hasSeatMapEnabled && !this._sectionsService.isCurrentSectionGeneralAdmission()) {
      this._sectionsService.unselectSectionSync();
    } else {
      this._seatSelectionState.setSelectState();
    }
    this.unselectAllMapSections();

    const subscription = this._sectionsService.selectedSectionData$.pipe(take(1)).subscribe(section => {
      if (!section) {
        return;
      }

      this.viewer2DRef?.service.setAvailable('section', section.sectionID.toString());
      this.selectNode(section.sectionID.toString());
      this._timerService.isCheckoutCanceled = false;
      const seatViewerService = this._injector.get(SeatViewerService);
      seatViewerService._isSeatMapVisible.next(false);
    });

    this.subscriptions.push(subscription);
  };

  /**
   * Fetches and sets section availability data using the SectionsAPI service.
   */
  private fetchAndSetSectionAvailability = () => {
    const selectedEventID = this._eventService?.selectedEvent?.id;
    const selectedTickets = this._eventTicketsService.getSelectedTicketsData();

    this._sectionsApi
      .fetchSectionAvailability(selectedEventID!, selectedTickets)
      .pipe(take(1), catchError(this.onAvailabilityFetchError))
      .subscribe(availabilityData => {
        if (!availabilityData) {
          return;
        }

        const availabilityArray = Object.values(availabilityData);
        this.setSectionAvailability(availabilityArray);
      });
  };

  public onAvailabilityFetchError = (error: { error: { code: string; message: string } }) => {
    const config = this.getErrorConfiguration(error)!;
    this._router.navigate(['/events/tickets']);
    this._modalService.openModal(config);
    return of();
  };

  private onTopviewProviderError = () => {
    const config = {
      type: 'app',
      text: 'The seat provider is currently unavailable due to maintenance. Please try again later.',
      buttonText: 'Reload',
      buttonAction: () => {
        this._modalService.closeModal();
        location.reload();
      },
    };

    this._router.navigate(['/events/tickets']);
    this._modalService.openModal(config);
    return of();
  };

  /**
   * Determines the appropriate configuration for the modal based on the error message.
   * @param err - The error object containing the error code and message.
   * @returns {Object} - An object containing the modal configuration.
   */
  private getErrorConfiguration(error: { error?: { code: string; message: string } }) {
    const formattedErrorMessage = getMessageError(error.error!);
    return {
      type: 'app',
      text: formattedErrorMessage!,
      buttonText: 'Close',
      buttonAction: () => {
        this._modalService.closeModal();
      },
    };
  }

  /**
   * Sets the availability of sections in the viewer and updates related services.
   * @param availabilityData Array of SectionModels indicating availability of sections.
   */
  private setSectionAvailability = (availabilityData: SectionModel[]) => {
    const computedAvailability = this._exchangeService.getComputedAvailabilityFromSectionAvailability(availabilityData);

    this._filterService.setSectionPriceRange(computedAvailability);
    const isAvailablityOnViewerSet = this.setTopviewAvailability(computedAvailability);

    if (availabilityData.length === 0 || !isAvailablityOnViewerSet) {
      this._router.navigate(['/events/tickets']);

      const config = {
        type: 'error',
        text: 'No valid exchangeable tickets found for this event.',
        buttonText: 'Close',
        buttonAction: () => {
          this._modalService.closeModal();
        },
      };

      this._modalService.openModal(config);
      return;
    }

    if (isAvailablityOnViewerSet) {
      this.setIsMapLoading(false);
      if (this._sectionsService.getAvailableSectionData() == null) {
        this._sectionsService.setAvailableSectionData(availabilityData);
      }
    }
  };

  /**
   * Configures the viewer flags and scaling factors for optimal user experience.
   * @param viewer The MapViewerComponent instance being configured.
   */
  private configureTopViewFlags = (viewer: MapViewerComponent) => {
    viewer.service.flags.fixed_aspect_ratio = false;
    viewer.service.flags.automatic_hover = false;
    viewer.service.min_scaling_factor = 2;
    viewer.service.max_scaling_factor = 6;
  };

  private setupGeneralAdmissionSections = (viewer: MapViewerComponent) => {
    const nodes = viewer.service.getNodesByType('general_admission');
    this._sectionsService.setGeneralAdmissionSections(nodes?.map(node => node.id) || []);
  };
}
